import { bound } from "@frui.ts/helpers/dist";
import type { ScreenBase } from "@frui.ts/screens";
import { Router, ConductorOneChildActive, HashNavigationAdapter } from "@frui.ts/screens";
import { computed, observable } from "mobx";
import EventBus from "../services/eventBus";
import LocalizationService from "../services/localizationService";
import NewVersionService from "../services/newVersionService";
import AppRouter from "../services/router";
import SecurityService from "../services/securityService";
import AdministrationViewModel from "./administration/administrationViewModel";
import AlertsViewModel from "./alertsViewModel";
import CallCenterViewModel from "./callCenter/callCenterViewModel";
import EnforcementViewModel from "./enforcement/enforcementViewModel";
import FinanceViewModel from "./finance/financeViewModel";
import TimeChargesViewModel from "./timeCharges/timeChargesViewModel";
import UsersModuleViewModel from "./users/usersModuleViewModel";
import PriceListService from "../services/priceListService";

@Router.registerRoute({
  route: "",
  children: [
    SecurityService,
    TimeChargesViewModel,
    CallCenterViewModel,
    FinanceViewModel,
    EnforcementViewModel,
    AdministrationViewModel,
    UsersModuleViewModel,
  ],
})
export default class RootViewModel extends ConductorOneChildActive<ScreenBase> {
  @observable loading = false;
  private urlAdapter: HashNavigationAdapter;
  private unsubscriptions: Array<() => void> = [];

  constructor(
    private appRouter: AppRouter,
    private localizationService: LocalizationService,
    private securityService: SecurityService,
    private priceListService: PriceListService,
    private eventBus: EventBus,
    private newVersionService: NewVersionService,
    private timeChargesViewModel: TimeChargesViewModel,
    private callCenterViewModel: CallCenterViewModel,
    private financeViewModel: FinanceViewModel,
    private enforcementViewModel: EnforcementViewModel,
    private administrationViewModel: AdministrationViewModel,
    private usersViewModel: UsersModuleViewModel,
    public alertsViewModel: AlertsViewModel
  ) {
    super();

    if (this.timeChargesViewModel.children.length) {
      this.children.push(this.timeChargesViewModel);
    }

    if (this.callCenterViewModel.children.length) {
      this.children.push(this.callCenterViewModel);
    }

    if (this.financeViewModel.children.length) {
      this.children.push(this.financeViewModel);
    }

    if (this.enforcementViewModel.children.length) {
      this.children.push(this.enforcementViewModel);
    }

    if (this.administrationViewModel.children.length) {
      this.children.push(this.administrationViewModel);
    }

    if (this.usersViewModel.children.length) {
      this.children.push(this.usersViewModel);
    }

    this.urlAdapter = new HashNavigationAdapter();
    this.appRouter.start(this);
  }

  protected async onInitialize() {
    await this.urlAdapter.start(this);
    await this.priceListService.startInitialization();

    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }

    return super.onDeactivate(isClosing);
  }

  protected findNavigationChild(name?: string) {
    if (!name) {
      return this.children[0];
    }

    return super.findNavigationChild(name);
  }

  closeChild() {
    // children cannot be closed
    return false;
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  @computed
  get logoutUrl() {
    return this.securityService.logoutUrl;
  }

  @bound
  logout() {
    this.loading = true;
    return this.securityService.logout();
  }

  @bound
  onLogoutComplete() {
    this.loading = false;
    this.securityService.onLogoutComplete();
  }

  @computed
  get user() {
    return this.securityService.user;
  }

  async tryActivateChild(child: ScreenBase) {
    await super.tryActivateChild(child);
    this.updateNewVersionIfAvailable();
  }

  @bound
  updateNewVersionIfAvailable() {
    this.newVersionService.updateAppIfNewVersionIsAvailable();
  }
}
